/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  Button,
  Form,
  Input,
  InputNumber,
  Card,
  Switch,
  Space,
  message,
} from 'antd'
import { CloseOutlined, SaveOutlined } from '@ant-design/icons'

import api from '~/services/api'

import { useCompanies } from '~/hooks/Companies/useCompanies'
import { useScale } from '~/hooks/Scales/useScale'
import { useListScales } from '~/hooks/Scales/useListScales'
import { useLocale } from '~/hooks/locale/useLocale'

import { ShowError } from '~/utils/errors/apiErrors'
import { translate } from '~/utils/locale'

interface NewScaleValues {
  description: string
  is_enabled: boolean
  operating_range: number
  title: string
}

const ScaleGeneralUpdate: React.FC = () => {
  const [form] = Form.useForm()

  const { scaleId } = useParams<{ scaleId: string }>()

  const { selectedCompany } = useCompanies()
  const { scaleData, scaleRefetch, setIsEdit } = useScale()
  const { scalesRefetch } = useListScales()
  const { locale } = useLocale()

  const [confirmLoading, setConfirmLoading] = useState(false)

  const onFinish = async (values: NewScaleValues) => {
    try {
      setConfirmLoading(true)
      await api.put(`/company/${selectedCompany}/schedule/${scaleId}`, values)
      message.success(translate('ScalesGeneralForm.successMessage'))
      scaleRefetch()
      scalesRefetch()
      setIsEdit(false)
      setConfirmLoading(false)
    } catch (err: any) {
      ShowError(
        err.message,
        translate('ScalesGeneralForm.errorMessage'),
        locale,
      )

      setConfirmLoading(false)
    }
  }

  return (
    <Form
      form={form}
      name="scaleForm"
      onFinish={onFinish}
      initialValues={scaleData}
      layout="vertical"
    >
      <Card
        bordered={false}
        title={<b>{translate('ScalesGeneralForm.cardTitle')}</b>}
        extra={
          <Space>
            <Form.Item style={{ margin: '0 auto' }}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={confirmLoading}
              >
                {translate('ScalesGeneralForm.saveButton')}
              </Button>
            </Form.Item>
            <Button
              type="default"
              icon={<CloseOutlined style={{ fontSize: 14 }} />}
              onClick={() => setIsEdit(false)}
            />
          </Space>
        }
        style={{
          display: 'inline-block',
          width: 'calc(100% - 20px)',
        }}
      >
        <Form.Item
          name="title"
          label={translate('ScalesGeneralForm.titleLabel')}
          style={{
            display: 'inline-block',
            width: 'calc(50% - 13.4px)',
          }}
          rules={[
            {
              required: true,
              message: translate('ScalesGeneralForm.titleRule'),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={translate('ScalesGeneralForm.operatingRangeLabel')}
          name="operating_range"
          style={{
            display: 'inline-block',
            width: 'calc(25% - 13.3px)',
            marginLeft: '20px',
          }}
          rules={[
            {
              required: true,
              message: translate('ScalesGeneralForm.operatingRangeRule'),
            },
          ]}
        >
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label={translate('ScalesGeneralForm.isEnabledLabel')}
          name="is_enabled"
          valuePropName="checked"
          style={{
            display: 'inline-block',
            width: 'calc(25% - 13.3px)',
            marginLeft: '20px',
          }}
        >
          <Switch
            checkedChildren={translate('ScalesGeneralForm.isEnabledActive')}
            unCheckedChildren={translate('ScalesGeneralForm.isEnabledInactive')}
          />
        </Form.Item>

        <Form.Item
          name="description"
          style={{
            display: 'inline-block',
            width: '100%',
          }}
          label={translate('ScalesGeneralForm.descriptionLabel')}
        >
          <Input.TextArea rows={2} />
        </Form.Item>
      </Card>
    </Form>
  )
}

export default ScaleGeneralUpdate
